import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy, 
  where 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SiteData, BlogPost } from '../types';

const SITE_DATA_COLLECTION = 'siteData';
const BLOG_POSTS_COLLECTION = 'blogPosts';

export const firebaseService = {
  // Site Data Operations
  async getSiteData(): Promise<SiteData | null> {
    try {
      const docRef = doc(db, SITE_DATA_COLLECTION, 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as SiteData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching site data:', error);
      throw error;
    }
  },

  async updateSiteData(data: SiteData): Promise<void> {
    try {
      const docRef = doc(db, SITE_DATA_COLLECTION, 'main');
      await setDoc(docRef, data);
    } catch (error) {
      console.error('Error updating site data:', error);
      throw error;
    }
  },

  // Blog Posts Operations
  async getBlogPosts(): Promise<BlogPost[]> {
    try {
      const q = query(
        collection(db, BLOG_POSTS_COLLECTION),
        orderBy('publishedDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BlogPost[];
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      throw error;
    }
  },

  async getBlogPostById(id: string): Promise<BlogPost | null> {
    try {
      const docRef = doc(db, BLOG_POSTS_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as BlogPost;
      }
      return null;
    } catch (error) {
      console.error('Error fetching blog post:', error);
      throw error;
    }
  },

  async getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
    try {
      const q = query(
        collection(db, BLOG_POSTS_COLLECTION),
        where('slug', '==', slug)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as BlogPost;
      }
      return null;
    } catch (error) {
      console.error('Error fetching blog post by slug:', error);
      throw error;
    }
  },

  async createBlogPost(post: Omit<BlogPost, 'id'>): Promise<string> {
    try {
      const docRef = doc(collection(db, BLOG_POSTS_COLLECTION));
      await setDoc(docRef, post);
      return docRef.id;
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error;
    }
  },

  async updateBlogPost(id: string, post: Partial<BlogPost>): Promise<void> {
    try {
      const docRef = doc(db, BLOG_POSTS_COLLECTION, id);
      await updateDoc(docRef, post);
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  },

  async deleteBlogPost(id: string): Promise<void> {
    try {
      const docRef = doc(db, BLOG_POSTS_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  }
};

export default firebaseService;
